import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useApi } from '@/shared/api';
import { can, useAuth } from '@/shared/auth';
import { PageHeader } from '@/shared/components';
import { useOnline } from '@/shared/hooks/useOnline';
import { PRIVILEGES } from '@stayon/auth-core';
import { AssigneeCard } from './assign/AssigneeCard';
import { buildPhotoSlots, isFinalStatus, slotProgress } from './capture/photoSlotModel';
import { useSlotCapture } from './capture/useSlotCapture';
import { cleaningTaskFromDetail } from './cleaningDetailAdapter';
import { useTaskStep } from './my-tasks/useTaskStep';
import { CleaningReportsSection } from './reports/CleaningReportsSection';
import { FinishCard } from './work/FinishCard';
import { ForeignTaskNotice } from './work/ForeignTaskNotice';
import { RoomGrid } from './work/RoomGrid';
import { useTaskClock } from './work/useTaskClock';
import { WorkFooter } from './work/WorkFooter';
import { openRoomsLine, workStep } from './work/workModel';
import { WorkState } from './work/WorkState';

/** Die Aufgabe (Board B): alle Räume auf einen Blick, Notiz der Leitung, Meldungen, fester Fuß. */
export function CleaningDetailPage() {
  const { id } = useParams({ from: '/_admin/cleaning/$id' });
  const api = useApi();
  const { user } = useAuth();
  const online = useOnline();
  const capture = useSlotCapture(id);
  const [failure, setFailure] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const step = useTaskStep({ onFailure: setFailure });
  const query = useQuery({
    queryKey: ['cleaning', 'detail', id],
    queryFn: () => api.cleaning.getDetail(id),
  });
  const clock = useTaskClock(query.data?.summary);

  if (!query.data) return <WorkState query={query} />;
  const detail = query.data;
  const summary = detail.summary;
  const task = cleaningTaskFromDetail(detail);
  const slots = buildPhotoSlots(task);
  const progress = slotProgress(slots);
  // Wer zuteilt, arbeitet ohne Übernahme und darf Fotos schon vor dem Start (Server, wie bisher).
  const mayManage = can(user, PRIVILEGES.cleaningAssignmentsManage);
  const next = workStep(summary, mayManage);
  const notStarted = summary.status === 'Pending' && !mayManage;
  const internal =
    summary.propertyInternalName && summary.propertyInternalName !== summary.propertyName
      ? `Intern: ${summary.propertyInternalName}`
      : null;

  return (
    <div className="mx-auto flex min-h-full w-full max-w-xl flex-col gap-3">
      <PageHeader
        className="mb-1"
        title={summary.propertyName}
        description={[internal, clock.facts].filter(Boolean).join(' · ')}
        tools={
          <span className="rounded-pill bg-mint text-forest-deep flex h-10 items-center px-3.5 text-sm font-extrabold">
            {summary.photoSlotCount > 0
              ? `${summary.filledPhotoSlotCount} von ${summary.photoSlotCount} Fotos`
              : 'ohne Fotos'}
          </span>
        }
      />
      <WorkState query={query} inline />
      {!online && (
        <p
          role="status"
          className="bg-amber-tint text-amber-ink rounded-tile p-3 text-[13px] font-semibold"
        >
          Keine Verbindung. Fotos und „Erledigt“ gehen erst wieder mit Netz – was schon hochgeladen
          ist, bleibt.
        </p>
      )}
      {summary.canViewDetails ? (
        <RoomGrid
          taskId={id}
          slots={slots}
          capture={capture}
          captureLocked={!online || isFinalStatus(task) || !summary.canMutate || notStarted}
          retakeLocked={!online || summary.status === 'Cancelled' || !summary.canMutate}
          willComplete={progress.total > 0 && progress.filled + 1 >= progress.total}
          willBeIssue={task.damages.some((damage) => damage.acknowledgedAt === null)}
        />
      ) : (
        <ForeignTaskNotice task={summary} />
      )}
      {detail.notes && (
        <p className="bg-amber-tint text-amber-ink rounded-control p-3 text-[13px] leading-relaxed">
          <b>Notiz der Leitung:</b> {detail.notes}
        </p>
      )}
      <FinishCard
        arrival={summary.nextArrival}
        notes={next === 'finish' ? notes : null}
        completionNotes={detail.completionNotes}
        onNotes={setNotes}
      />
      {summary.canViewDetails && mayManage && <AssigneeCard task={task} />}
      {summary.canViewDetails && (summary.canMutate || task.damages.length > 0) && (
        <CleaningReportsSection
          task={task}
          locked={
            !online
              ? 'Keine Verbindung – Melden geht erst wieder mit Netz.'
              : notStarted
                ? 'Melden geht ab dem Start der Aufgabe.'
                : summary.status === 'Cancelled'
                  ? 'Die Aufgabe entfällt.'
                  : null
          }
        />
      )}
      <WorkFooter
        task={summary}
        step={next}
        openRooms={openRoomsLine(slots)}
        online={online}
        busy={step.isPending}
        failure={failure}
        timeZone={clock.timeZone}
        onStep={(chosen) => {
          setFailure(null);
          step.mutate({
            task: summary,
            step: chosen,
            notes,
            claim: summary.canClaim && !mayManage,
          });
        }}
      />
    </div>
  );
}
